/**
 * IsTestToggle — admin-only escape hatch to flip the `is_test` flag on
 * a candidate profile.
 *
 * Rationale (CEO brief 2026-07-29 point 1) : the auto-detection at
 * creation time uses conservative heuristics (email @example.com,
 * first_name ITER, etc.). If a REAL candidate is ever flagged by
 * mistake, admins need a manual override — otherwise that candidate
 * silently disappears from Rattrapage, PayFit export, and /service/v1.
 *
 * Behaviour:
 *   - Not admin → renders the same read-only "🧪 CANDIDAT TEST" badge
 *     everyone already knows (or nothing when `is_test=false`).
 *   - Admin + is_test=true → clickable pill "Retirer le flag test"
 *     with a confirmation modal.
 *   - Admin + is_test=false → discreet "Marquer comme test" button
 *     for symmetry (also gated by confirm).
 *   - Every flip is journalled server-side into `is_test_history[]`
 *     (see routes/candidates.py::update_candidate).
 *   - Once a candidate has a `code_rd`, flipping BACK to is_test=true
 *     is refused by the server (409) — the toggle shows a disabled
 *     tooltip explaining why.
 */
import { useState } from "react";
import { FlaskConical, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { hasRole } from "../../lib/roles";


export default function IsTestToggle({ candidate, user, onChange }) {
    const isAdmin = hasRole(user, "admin");
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const flagged = Boolean(candidate.is_test);
    const lockedByCode = flagged === false && Boolean(candidate.code_rd);
    // History count for the tooltip (last flip date).
    const history = candidate.is_test_history || [];
    const lastFlip = history[history.length - 1];

    // Non-admin: keep the existing read-only badge intact so nothing
    // changes visually for pre-recruteurs / referents.
    if (!isAdmin) {
        if (!flagged) return null;
        return (
            <span
                data-testid="profile-test-badge"
                className="text-[10px] font-bold uppercase tracking-widest bg-shariti-orange text-white px-2.5 py-1 rounded-full"
            >
                🧪 Candidat test
            </span>
        );
    }

    async function apply() {
        setSaving(true);
        try {
            await api.patch(`/candidates/${candidate.id}`, {
                is_test: !flagged,
            });
            toast.success(
                !flagged
                    ? "Candidat marqué comme test (invisible du Rattrapage & de l'API)"
                    : "Flag test retiré — le candidat redevient un collaborateur normal"
            );
            setConfirmOpen(false);
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Modification échouée");
        } finally {
            setSaving(false);
        }
    }

    // Admin + flagged: pill in orange, click to unflag (with confirmation).
    if (flagged) {
        return (
            <>
                <button
                    type="button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={saving}
                    data-testid="profile-is-test-toggle-remove"
                    title={
                        lastFlip
                            ? `Dernier changement : ${lastFlip.by_name || "?"}, ${new Date(lastFlip.at).toLocaleDateString("fr-FR")}`
                            : "Retirer le flag « candidat test » (admin uniquement)"
                    }
                    className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-shariti-orange text-white px-2.5 py-1 rounded-full hover:bg-shariti-orange/90 disabled:opacity-60 transition"
                >
                    <FlaskConical className="h-3 w-3" />
                    Candidat test
                    <span className="text-[9px] opacity-70 pl-0.5">· retirer</span>
                </button>
                {confirmOpen && (
                    <ConfirmDialog
                        title="Retirer le flag « candidat test » ?"
                        body={
                            <>
                                Ce candidat redeviendra un collaborateur normal :
                                visible dans le <strong>Rattrapage hired</strong>, dans
                                l'export <strong>PayFit</strong>, et dans l'API
                                inter-plateforme <strong>/service/v1</strong> s'il est
                                hired avec un contrat actif. L'action est journalisée
                                (qui / quand) dans l'historique du candidat.
                            </>
                        }
                        ctaLabel="Retirer le flag"
                        ctaTone="orange"
                        onCancel={() => setConfirmOpen(false)}
                        onConfirm={apply}
                        saving={saving}
                        testId="profile-is-test-confirm-remove"
                    />
                )}
            </>
        );
    }

    // Admin + not flagged + candidate has a code_rd → nothing to do
    // here (the toggle is definitively locked; the code badge shown
    // next to the name IS the visible signature of that lock).
    if (lockedByCode) {
        return null;
    }

    // Admin + not flagged: discreet "Marquer comme test" button.
    return (
        <>
            <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={saving}
                data-testid="profile-is-test-toggle-add"
                title="Marquer ce candidat comme candidat de test"
                className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border border-slate-300 bg-white text-slate-600 hover:border-shariti-orange hover:text-shariti-orange px-2.5 py-1 rounded-full disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
                <FlaskConical className="h-3 w-3" />
                Marquer comme test
            </button>
            {confirmOpen && (
                <ConfirmDialog
                    title="Marquer ce candidat comme test ?"
                    body={
                        <>
                            Ce candidat deviendra <strong>invisible</strong> du Rattrapage,
                            de l'export PayFit et de l'API /service/v1. Il ne pourra
                            plus recevoir de code RD tant que le flag reste actif.
                            Utile pour les comptes de démo ou pour retirer un candidat
                            de test qui aurait glissé à travers.
                        </>
                    }
                    ctaLabel="Marquer comme test"
                    ctaTone="slate"
                    onCancel={() => setConfirmOpen(false)}
                    onConfirm={apply}
                    saving={saving}
                    testId="profile-is-test-confirm-add"
                />
            )}
        </>
    );
}


/** Minimal confirmation modal — matches the modal look-and-feel used
 *  elsewhere in the app (rounded-2xl, backdrop blur, orange CTA). */
function ConfirmDialog({ title, body, ctaLabel, ctaTone = "orange", onCancel, onConfirm, saving, testId }) {
    const ctaClass = ctaTone === "orange"
        ? "bg-shariti-orange hover:bg-shariti-orange/90 text-white"
        : "bg-slate-800 hover:bg-slate-900 text-white";
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            data-testid={testId}
            onClick={onCancel}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-black text-shariti-black">{title}</h3>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="p-1 rounded-full hover:bg-slate-100 transition"
                    >
                        <X className="h-4 w-4 text-slate-400" />
                    </button>
                </div>
                <div className="text-sm text-slate-600 mb-6">{body}</div>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-black uppercase tracking-wider transition"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={saving}
                        data-testid={`${testId}-cta`}
                        className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider transition disabled:opacity-60 flex items-center gap-2 ${ctaClass}`}
                    >
                        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {ctaLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
